import React, { useEffect, useState, useCallback } from 'react';
import JsonLd from '../components/JsonLd';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  BarChart3,
  Bot,
  Brain,
  BriefcaseBusiness,
  CheckCircle2,
  CheckCheck,
  ChevronDown,
  Clock,
  Cloud,
  Cog,
  Database,
  DollarSign,
  Fingerprint,
  Frown,
  Headphones,
  Hourglass,
  Info,
  KeyRound,
  Laptop,
  Layers,
  Link2Off,
  Lock,
  Mail,
  Mic,
  Monitor,
  Network,
  Phone,
  PhoneCall,
  PiggyBank,
  Plug,
  Repeat,
  Rocket,
  Scale,
  Server,
  Shield,
  ShieldCheck,
  Smile,
  SquareStack,
  Ticket,
  Timer,
  TrendingUp,
  TriangleAlert,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  UserRoundCog,
  Workflow,
  Zap,
  Star,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const sections = [
  { id: 'opening', label: 'Overview' },
  { id: 'executive', label: 'Executive' },
  { id: 'problem', label: 'Problem' },
  { id: 'platform', label: 'Platform' },
  { id: 'architecture', label: 'Architecture' },
  { id: 'use-cases', label: 'Use Cases' },
  { id: 'process', label: 'Process Flow' },
  { id: 'workflow', label: 'Workflow' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'telephony', label: 'Telephony' },
  { id: 'security', label: 'Security' },
  { id: 'impact', label: 'Impact' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'summary', label: 'Summary' },
  { id: 'comparison', label: 'Comparison' },
];

const AIVoiceAgentPage = () => {
  const [activeSection, setActiveSection] = useState('opening');

  const handleScroll = useCallback(() => {
    const offsets = sections.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return { id, top: Infinity };
      return { id, top: el.getBoundingClientRect().top };
    });
    let current = offsets[0]?.id;
    for (const { id, top } of offsets) {
      if (top <= 120) current = id;
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /* ─── Data ─── */
  const capabilities = [
    'Understands employee requests in natural language (voice-first)',
    'Authenticates users securely (MFA / policy-based verification)',
    'Resolves common IT requests instantly (L1 self-service via voice)',
    'Automates backend actions across ITSM, Identity, Email, HR, Endpoint tools',
    'Creates & updates tickets automatically when needed',
    'Escalates to humans when required, with full call summary and context',
  ];

  const stats = [
    { number: '70%', label: 'Tickets raised via Email', desc: 'Slow response channel' },
    { number: '40%', label: 'Handled by L1 Desk', desc: 'High manual workload' },
    { number: '50%', label: 'Eligible for Automation', desc: 'Repetitive tasks' },
    { number: '20%', label: 'Identity & Access', desc: 'Password resets / MFA' },
  ];

  const solutions = [
    { icon: PhoneCall, title: '24/7 AI Voice IT Support', desc: 'Always-on voice assistant handling employee requests' },
    { icon: Workflow, title: 'End-to-end Workflow Automation', desc: 'Automated execution across enterprise systems' },
    { icon: Layers, title: '50+ Enterprise Integrations', desc: 'Ready connectors for ITSM, IAM, HR, and more' },
    { icon: BarChart3, title: 'Measurable Ticket Reduction', desc: '40-50% deflection of repetitive L1 requests' },
  ];

  const problems = [
    { icon: KeyRound, text: 'Manual password resets and unlock requests' },
    { icon: Repeat, text: 'High ticket reassignment rate (~40%+)' },
    { icon: Timer, text: 'Delays in onboarding and offboarding' },
    { icon: Repeat, text: 'Repetitive L1 support workload' },
    { icon: Clock, text: 'Inconsistent SLA adherence' },
    { icon: DollarSign, text: 'High operational costs' },
  ];

  const platformFeatures = [
    'Understands natural language',
    'Authenticates users securely (MFA)',
    'Collects required structured information',
    'Executes backend workflows',
    'Creates and updates tickets automatically',
    'Provides real-time status updates',
  ];

  const channels = [
    { icon: Mic, label: 'Voice' },
    { icon: Phone, label: 'IVR' },
    { icon: Monitor, label: 'Teams' },
    { icon: PhoneCall, label: 'Telephony' },
  ];

  const useCases = [
    {
      icon: Fingerprint,
      title: 'Identity & Access Management',
      items: ['Password Reset', 'Account Unlock', 'MFA Enrollment', 'Distribution List Management', 'Role & Access Changes'],
      gradient: 'from-indigo-500 to-purple-500',
    },
    {
      icon: UserCog,
      title: 'Employee Lifecycle',
      items: ['Automated Onboarding', 'HR-triggered Offboarding', 'Title Changes / Transfers'],
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: Laptop,
      title: 'Hardware Management',
      items: ['Laptop Provisioning', 'Asset Replacement', 'Equipment Returns'],
      gradient: 'from-violet-500 to-indigo-500',
    },
    {
      icon: SquareStack,
      title: 'Software Management',
      items: ['Install Requests', 'License Validation', 'Application Access', 'Issue Diagnostics'],
      gradient: 'from-indigo-600 to-violet-600',
    },
  ];

  const processSteps = [
    'User calls IT Helpdesk',
    'AI authenticates via MFA',
    'Identity verified (OTP / AD validation)',
    'Password reset executed via Azure AD API',
    'Ticket auto-created & auto-closed',
    'User receives confirmation',
  ];

  const workflowItems = [
    { icon: Layers, text: 'Dynamic question matrix per category' },
    { icon: CheckCircle2, text: 'Validation rules (ID formats, asset tags, etc.)' },
    { icon: UserCheck, text: 'Automated approval routing' },
    { icon: Network, text: 'Multi-system execution' },
    { icon: Clock, text: 'SLA tracking and escalation' },
    { icon: CheckCircle2, text: 'Auto-closure logic' },
  ];

  const integrationCategories = [
    { icon: UserCog, label: 'HR Systems', tags: ['Workday', 'SAP', 'Oracle HCM', 'ADP'] },
    { icon: DollarSign, label: 'Finance', tags: ['SAP', 'Oracle', 'NetSuite', 'Dynamics'] },
    { icon: BriefcaseBusiness, label: 'CRM', tags: ['Salesforce', 'Tableau', 'Custom Apps'] },
    { icon: Ticket, label: 'IT Service Management', tags: ['ServiceNow', 'Jira', 'BMC', 'Freshservice'] },
    { icon: Mail, label: 'Collaboration', tags: ['Microsoft 365', 'Slack', 'Teams', 'Google Workspace'] },
    { icon: BarChart3, label: 'Productivity', tags: ['Exchange', 'SharePoint', 'OneDrive'] },
    { icon: Shield, label: 'Identity & Access', tags: ['Azure AD', 'Active Directory', 'Okta', 'Ping', 'Duo'] },
    { icon: Cloud, label: 'Cloud & Infrastructure', tags: ['Azure', 'AWS', 'GCP', 'REST APIs', 'SQL'] },
  ];

  const telephonyProviders = [
    { name: 'Avaya', logo: '/icons/avaya.png' },
    { name: 'Cisco', logo: '/icons/cisco.png' },
    { name: 'Genesys', logo: '/icons/genesys.png' },
    { name: 'Five9', logo: '/icons/five9.png' },
    { name: 'Twilio', logo: '/icons/twilio.png' },
    { name: 'SBC Integrations', logo: null },
    { name: 'SIP / SIP-RTP', logo: null },
    { name: 'UCaaS / CCaaS', logo: null },
  ];

  const securityCards = [
    { icon: ShieldCheck, title: 'MFA-based Authentication', desc: 'Multi-factor user verification' },
    { icon: UserCheck, title: 'Role-based Access Control', desc: 'Granular permission management' },
    { icon: BarChart3, title: 'Audit Logging & Traceability', desc: 'Complete action history' },
    { icon: Lock, title: 'Encryption', desc: 'Data encrypted in transit and at rest' },
    { icon: Network, title: 'Zero Trust Aligned', desc: 'Modern security framework' },
    { icon: CheckCircle2, title: 'Compliance-ready Deployment', desc: 'Designed for enterprise data governance and GDPR-aligned data handling' },
  ];

  const impactCards = [
    { icon: BarChart3, number: '40\u201350%', label: 'Ticket Deflection' },
    { icon: Users, number: '30\u201350%', label: 'Reduction in L1 Workload' },
    { icon: Clock, number: '24/7', label: 'Support Availability' },
    { icon: Rocket, number: 'Faster', label: 'Onboarding Cycles' },
    { icon: Repeat, number: 'Reduced', label: 'Ticket Reassignment' },
    { icon: Zap, number: 'Improved', label: 'SLA Performance' },
    { icon: DollarSign, number: 'Lower', label: 'Operational Cost' },
  ];

  const phases = [
    {
      number: 1,
      title: 'Quick Wins',
      timeline: '8\u201312 Weeks',
      items: ['Password Reset', 'Account Unlock', 'Distribution Lists', 'Software Install Automation'],
    },
    {
      number: 2,
      title: 'Process Automation',
      timeline: 'Next Phase',
      items: ['Onboarding / Offboarding', 'Hardware Lifecycle Automation', 'HR-driven workflows'],
    },
    {
      number: 3,
      title: 'AI Optimization',
      timeline: 'Advanced Features',
      items: ['Email classification and routing', 'Predictive ticket assignment', 'Knowledge-based auto-resolution'],
    },
  ];

  const executiveTakeaways = [
    { bold: 'AI Voice IT Helpdesk Assistant', text: 'provides 24/7 support through natural conversation and secure authentication' },
    { bold: 'Workflow automation', text: 'executes actions across enterprise systems to resolve common requests without human effort' },
    { bold: 'Integration-ready', text: 'via 50+ connectors (MCP + CData) across ITSM, IAM, Email, HR, Endpoint, ERP, and custom apps' },
    { bold: 'Operational impact:', text: 'typically 40\u201350% automation potential for repetitive L1 requests (varies by environment)' },
    { bold: 'Governed and secure:', text: 'RBAC, audit logs, approvals, and Zero Trust aligned deployment options' },
  ];

  const pilotScope = [
    'Password Reset + Account Unlock',
    'Distribution Lists / Group Membership',
    'Ticket create/update/close in ITSM',
    'Azure AD + M365/Exchange integration',
  ];

  const successMetrics = ['Deflection Rate', 'Time-to-Resolution', 'CSAT', 'SLA Compliance', 'L1 Workload Reduction'];

  const comparisonRows = [
    { criteria: 'Availability & Access', traditional: 'Business Hours (8x5)', digian: '24/7/365 Always-On', tradIcon: Clock, digianIcon: CheckCircle2, tradColor: 'red', digianColor: 'green' },
    { criteria: 'Response Time', traditional: '5-15 Minutes (Queue)', digian: 'Instant (<1 Second)', tradIcon: Hourglass, digianIcon: Zap, tradColor: 'yellow', digianColor: 'green' },
    { criteria: 'Scalability', traditional: 'Linear (Hire More Agents)', digian: 'Infinite (Elastic Cloud)', tradIcon: UserPlus, digianIcon: TrendingUp, tradColor: 'red', digianColor: 'green' },
    { criteria: 'Cost per Ticket', traditional: 'High ($25-$40/ticket)', digian: 'Low (~$5/ticket)', tradIcon: DollarSign, digianIcon: PiggyBank, tradColor: 'red', digianColor: 'green' },
    { criteria: 'Automation Capabilities', traditional: 'Limited (Scripts/Macros)', digian: 'End-to-End Workflow', tradIcon: TriangleAlert, digianIcon: Bot, tradColor: 'yellow', digianColor: 'green' },
    { criteria: 'User Experience (UX)', traditional: 'Frustrating (Wait Times)', digian: 'Seamless (Natural Voice)', tradIcon: Frown, digianIcon: Smile, tradColor: 'red', digianColor: 'green' },
    { criteria: 'Security & MFA Support', traditional: 'Manual Verification', digian: 'Integrated MFA / Biometric', tradIcon: Shield, digianIcon: Fingerprint, tradColor: 'yellow', digianColor: 'green' },
    { criteria: 'Integration Ecosystem', traditional: 'Siloed / Disconnected', digian: '50+ Native Connectors', tradIcon: Link2Off, digianIcon: Plug, tradColor: 'red', digianColor: 'green' },
    { criteria: 'Ticket Deflection Rate', traditional: '< 10% (Portal only)', digian: '40-50% (Voice + Action)', tradIcon: TrendingUp, digianIcon: CheckCheck, tradColor: 'red', digianColor: 'green' },
    { criteria: 'L1 Workload Reduction', traditional: 'High Burnout Risk', digian: '~50% Automation', tradIcon: Frown, digianIcon: UserRoundCog, tradColor: 'red', digianColor: 'green' },
  ];

  /* ─── Render ─── */
  return (
    <div className="min-h-screen bg-white relative">
      <SEOHead page="aiVoiceAgent" />
      <JsonLd schema={{"@context":"https://schema.org","@graph":[{"@type":"SoftwareApplication","name":"Digian AI Voice Agent","applicationCategory":"BusinessApplication","operatingSystem":"Cloud","description":"Deploy intelligent AI voice agents that handle inbound and outbound calls 24/7. Reduce call centre costs while improving customer satisfaction.","url":"https://digian.ai/ai-voice-agent","offers":{"@type":"Offer","seller":{"@type":"Organization","name":"Digian"}}},{"@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Which ITSM platforms do you integrate with?","acceptedAnswer":{"@type":"Answer","text":"Digian.ai AI Voice Agent integrates natively with ServiceNow, Jira Service Management, Freshservice, Zendesk, and more. Custom integrations are available on Enterprise plans."}},{"@type":"Question","name":"What telephony providers are supported?","acceptedAnswer":{"@type":"Answer","text":"We support Twilio, Genesys, Cisco, Avaya, RingCentral, and most SIP-based providers."}},{"@type":"Question","name":"How long does it take to deploy?","acceptedAnswer":{"@type":"Answer","text":"Standard deployments are live within 2–3 weeks. Enterprise deployments with custom integrations typically take 4–6 weeks with a dedicated implementation engineer."}},{"@type":"Question","name":"Can the AI escalate to a human agent?","acceptedAnswer":{"@type":"Answer","text":"Yes. The AI detects when a query requires human expertise and transfers seamlessly to a live agent, passing full call context, transcript, and ticket data."}},{"@type":"Question","name":"How is the AI trained on our data?","acceptedAnswer":{"@type":"Answer","text":"You provide your knowledge base, IT policies, and historical ticket data during onboarding. The AI is fine-tuned on your content without sharing data with other customers."}},{"@type":"Question","name":"How is data secured?","acceptedAnswer":{"@type":"Answer","text":"All voice data is encrypted in transit and at rest. We follow GDPR-aligned data handling practices, include full audit logging, and support on-premise deployment for Enterprise customers requiring full data sovereignty."}}]}]}} />

      {/* ── Sticky Timeline Sidebar (desktop only) ── */}
      <nav className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-40">
        <div className="relative flex flex-col items-start pl-1.5">
          {sections.map((sec, i) => {
            const isActive = activeSection === sec.id;
            const activeIdx = sections.findIndex(s => s.id === activeSection);
            const isPast = i < activeIdx;
            return (
              <button
                key={sec.id}
                onClick={() => scrollTo(sec.id)}
                className="relative flex items-center gap-3 group py-2"
              >
                <div
                  className={`relative z-10 w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                    isActive
                      ? 'bg-indigo-600 border-indigo-600 scale-125'
                      : isPast
                      ? 'bg-indigo-400 border-indigo-400'
                      : 'bg-white border-slate-300 group-hover:border-indigo-400'
                  }`}
                />
                <span
                  className={`text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                    isActive
                      ? 'text-indigo-600 font-semibold'
                      : isPast
                      ? 'text-indigo-400'
                      : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                >
                  {sec.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════
          Slide 1: Opening Context / Positioning
         ══════════════════════════════════════════════════════════ */}
      <section id="opening" className="relative overflow-hidden bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-blue-500/10 py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-6">
            <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
              AI Voice IT Helpdesk Assistant for{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-violet-600">
                Enterprise IT Operations
              </span>
            </h1>
            <p className="text-lg text-indigo-600 font-medium">
              Always-on voice support + backend workflow automation for faster L1 resolution
            </p>

            <div className="grid lg:grid-cols-5 gap-6">
              {/* Left: Key Message + What it does */}
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-indigo-50 border-l-4 border-indigo-600 rounded-r-xl p-6">
                  <p className="text-slate-800 leading-relaxed">
                    <strong className="text-indigo-600">digian.ai</strong> delivers an AI-powered IT Helpdesk Assistant that handles common employee IT requests end-to-end, through natural voice conversations, secure authentication, and automated execution across enterprise systems.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <Bot className="w-6 h-6 text-indigo-600" /> What it does:
                  </h3>
                  <div className="space-y-3">
                    {capabilities.map((cap, i) => (
                      <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                        className="flex items-start gap-3 p-3 bg-white rounded-xl hover:translate-x-1 transition-transform hover:shadow-md">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-800">{cap}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: Impact Box */}
              <motion.div {...fadeUp} className="lg:col-span-2 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl p-8 text-white shadow-lg self-start">
                <h3 className="text-2xl font-bold flex items-center gap-2 mb-6">
                  <Zap className="w-6 h-6" /> IMPACT
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-indigo-200 mb-2">GOAL</h4>
                    <p className="text-lg">Reduce repetitive L1 workload and accelerate resolution</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-indigo-200 mb-2">OUTCOME</h4>
                    <p className="text-lg">Better employee experience, improved SLAs, lower IT ops cost</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Where it fits: Architecture Flow */}
            <motion.div {...fadeUp} className="bg-slate-50 border-2 border-slate-200 rounded-xl p-8 mt-8">
              <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-6">WHERE IT FITS:</h4>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">
                {[
                  { icon: Users, label: 'Voice/Teams/IVR', highlight: false },
                  { icon: Headphones, label: 'AI Voice Assistant', highlight: 'primary' },
                  { icon: Cog, label: 'Automation Engine', highlight: 'dark' },
                  { icon: Server, label: 'Enterprise Systems', sublabel: 'ITSM \u2022 IAM \u2022 Email\nHR \u2022 Endpoint \u2022 Apps', highlight: false },
                ].map((box, i) => {
                  const Icon = box.icon;
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && (
                        <ArrowRight className="w-8 h-8 text-indigo-500 flex-shrink-0 rotate-0 md:rotate-0 rotate-90 md:!rotate-0" />
                      )}
                      <div className={`flex flex-col items-center gap-2 p-6 rounded-xl min-w-[160px] text-center transition-all hover:-translate-y-1 hover:shadow-lg ${
                        box.highlight === 'primary'
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-md'
                          : box.highlight === 'dark'
                          ? 'bg-gradient-to-br from-indigo-700 to-violet-700 text-white shadow-md'
                          : 'bg-white border-2 border-slate-200 text-slate-700'
                      }`}>
                        <Icon className="w-10 h-10" />
                        <div className="font-bold text-sm uppercase tracking-wide">{box.label}</div>
                        {box.sublabel && (
                          <div className="text-xs opacity-80 whitespace-pre-line">{box.sublabel}</div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 2: Executive Overview
         ══════════════════════════════════════════════════════════ */}
      <section id="executive" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Executive <span className="text-indigo-600">Overview</span>
            </h2>
            <h3 className="text-xl text-slate-700 font-semibold mb-10">Transforming IT Helpdesk with AI Voice & Intelligent Automation</h3>
          </motion.div>

          <h4 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" /> Modern Enterprises Face:
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {stats.map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl border-l-4 border-indigo-500 p-6 shadow-sm">
                <div className="text-4xl font-extrabold text-indigo-600">{s.number}</div>
                <div className="text-lg font-semibold text-slate-900 mt-1">{s.label}</div>
                <div className="text-sm text-slate-500">{s.desc}</div>
              </motion.div>
            ))}
          </div>

          <h4 className="text-lg font-semibold text-slate-800 mb-6 flex items-center gap-2">
            <Bot className="w-5 h-5 text-indigo-600" /> digian.ai delivers:
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {solutions.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border-2 border-slate-200 p-6 shadow-sm hover:shadow-md transition-shadow">
                  <Icon className="w-10 h-10 text-indigo-600 mb-3" />
                  <h4 className="text-lg font-bold text-slate-900 mb-1">{s.title}</h4>
                  <p className="text-sm text-slate-600">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 3: The Problem
         ══════════════════════════════════════════════════════════ */}
      <section id="problem" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              The Problem with <span className="text-indigo-600">Traditional IT Support</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Current IT Helpdesk Challenges</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-4 p-5 bg-white rounded-xl border-l-4 border-indigo-500">
                  <Icon className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-800 font-medium">{p.text}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp} className="mt-10 bg-indigo-50 border-2 border-indigo-500 rounded-xl p-6">
            <h3 className="text-xl font-bold text-indigo-600 flex items-center gap-2 mb-2">
              <ArrowRight className="w-6 h-6" /> Result:
            </h3>
            <p className="text-slate-800 font-medium text-lg">Service Desk overload, slow resolution, poor employee experience</p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 4: Platform Overview
         ══════════════════════════════════════════════════════════ */}
      <section id="platform" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              <span className="text-indigo-600">digian.ai</span> AI Voice IT Helpdesk Platform
            </h2>
            <p className="text-lg text-slate-600 mb-10">Intelligent Voice + Backend Automation</p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <h3 className="text-xl font-bold text-slate-900 mb-6">Our AI-powered voice agent:</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {platformFeatures.map((f, i) => (
                  <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-3 p-4 bg-indigo-50 rounded-xl">
                    <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                    <p className="text-slate-800 font-medium">{f}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div {...fadeUp} className="bg-white rounded-xl border-2 border-indigo-500 p-6 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-1">
                <Mic className="w-5 h-5 text-indigo-600" /> Available 24/7
              </h3>
              <span className="inline-block bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                ENTERPRISE-READY
              </span>
              <p className="text-sm text-slate-600 font-semibold mb-4">Always-on IT support</p>
              <div className="grid grid-cols-2 gap-3">
                {channels.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <div key={i} className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg">
                      <Icon className="w-5 h-5 text-indigo-600" />
                      <span className="text-sm font-medium text-slate-800">{c.label}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 5: Solution Architecture
         ══════════════════════════════════════════════════════════ */}
      <section id="architecture" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Solution <span className="text-indigo-600">Architecture Overview</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Employee touchpoints → AI/NLP → Workflow Engine → Enterprise Systems</p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-6">
            <motion.div {...fadeUp} className="bg-white rounded-xl border-2 border-indigo-500 p-6 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-indigo-600" /> Employee
              </h3>
              <ul className="space-y-2">
                {['Phone / IVR', 'Teams / UCaaS', 'Web / Mobile'].map((item) => (
                  <li key={item} className="pl-4 border-l-3 border-indigo-500 text-slate-600">{item}</li>
                ))}
              </ul>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border-2 border-purple-500 p-5 shadow-sm">
                  <h3 className="text-lg font-bold text-purple-600 flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5" /> AI Voice & NLP
                  </h3>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li className="pl-3 border-l-2 border-purple-400">Speech + NLU</li>
                    <li className="pl-3 border-l-2 border-purple-400">Intent detection</li>
                    <li className="pl-3 border-l-2 border-purple-400">User authentication</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl border-2 border-violet-500 p-5 shadow-sm">
                  <h3 className="text-lg font-bold text-violet-600 flex items-center gap-2 mb-3">
                    <Cog className="w-5 h-5" /> Automation Engine
                  </h3>
                  <ul className="space-y-1 text-sm text-slate-600">
                    <li className="pl-3 border-l-2 border-violet-400">Approvals + rules</li>
                    <li className="pl-3 border-l-2 border-violet-400">Workflow orchestration</li>
                    <li className="pl-3 border-l-2 border-violet-400">Ticket updates & closure</li>
                  </ul>
                </div>
              </div>
              <div className="bg-indigo-50 border-2 border-indigo-500 rounded-xl p-5">
                <h4 className="text-lg font-bold text-indigo-600 mb-3">CORE LAYERS</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Layers, label: 'AI Voice Layer' },
                    { icon: Network, label: 'Integration Layer' },
                    { icon: Workflow, label: 'Automation Orchestration Layer' },
                    { icon: Shield, label: 'Governance & Security Layer' },
                  ].map((l, i) => {
                    const Icon = l.icon;
                    return (
                      <div key={i} className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                        <Icon className="w-4 h-4 text-indigo-600" /> {l.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.2 }} className="bg-white rounded-xl border-2 border-indigo-500 p-6 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                <Database className="w-5 h-5 text-indigo-600" /> Enterprise Systems
              </h3>
              <ul className="space-y-2">
                {['Azure AD / AD / Okta', 'ITSM (ServiceNow/Jira)', 'Exchange / M365', 'HR (Workday/SAP)', 'CMDB / Endpoint', 'Custom apps'].map((item) => (
                  <li key={item} className="pl-4 border-l-3 border-indigo-500 text-slate-600">{item}</li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 6: Use Cases
         ══════════════════════════════════════════════════════════ */}
      <section id="use-cases" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-10">
              Core IT Helpdesk <span className="text-indigo-600">Automation Use Cases</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((uc, i) => {
              const Icon = uc.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className={`bg-gradient-to-br ${uc.gradient} rounded-xl p-6 text-white shadow-lg`}>
                  <h3 className="text-xl font-bold flex items-center gap-2 mb-4">
                    <Icon className="w-6 h-6" /> {uc.title}
                  </h3>
                  <ul className="space-y-2">
                    {uc.items.map((item, j) => (
                      <li key={j} className="text-white/90 flex items-center gap-2">
                        <span className="text-lg">&bull;</span> {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 7: Password Reset Example
         ══════════════════════════════════════════════════════════ */}
      <section id="process" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Example: <span className="text-indigo-600">Password Reset via Voice</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Fully Automated Flow</p>
          </motion.div>

          <div className="space-y-4">
            {processSteps.map((step, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }} className="relative">
                <div className="flex items-center gap-4 bg-white rounded-xl border-2 border-indigo-500 p-5 shadow-sm">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-slate-800 font-medium">{step}</p>
                </div>
                {i < processSteps.length - 1 && (
                  <div className="flex justify-center py-1 text-indigo-500">
                    <ChevronDown className="w-6 h-6" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-10 bg-indigo-50 border-2 border-indigo-500 rounded-xl p-6">
            <h3 className="text-xl font-bold text-indigo-600 mb-3">Result:</h3>
            <ul className="space-y-2">
              {['Instant resolution', 'Zero human intervention', 'Reduced Service Desk calls'].map((r) => (
                <li key={r} className="flex items-center gap-2 text-slate-800 font-semibold">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600" /> {r}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 8: Workflow Engine
         ══════════════════════════════════════════════════════════ */}
      <section id="workflow" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Workflow <span className="text-indigo-600">Automation Engine</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Intelligent Orchestration</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workflowItems.map((w, i) => {
              const Icon = w.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                  className="flex items-start gap-4 p-5 bg-indigo-50 rounded-xl border-l-4 border-indigo-500">
                  <Icon className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <p className="text-slate-800 font-medium">{w.text}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div {...fadeUp}
            className="mt-12 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-10 text-center text-white shadow-lg">
            <h3 className="text-lg font-semibold mb-2">Designed to Automate:</h3>
            <p className="text-4xl font-extrabold mb-2">~40–50%</p>
            <p className="text-xl font-semibold">of repetitive IT requests</p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 9: Enterprise Integrations (Animated Flow Diagram)
         ══════════════════════════════════════════════════════════ */}
      <section id="integrations" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Enterprise Integrations <span className="text-indigo-600">(50+ Ready Connectors)</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3">
              <span className="inline-block bg-indigo-600 text-white px-4 py-1.5 rounded-full text-sm font-bold">MCP Framework</span>
              <span className="inline-block bg-indigo-600 text-white px-4 py-1.5 rounded-full text-sm font-bold">CData AI Connectors</span>
            </div>
          </motion.div>

          {/* Animated SVG Flow Diagram — desktop */}
          <motion.div {...fadeUp} className="hidden lg:block relative w-full mt-10" style={{ aspectRatio: '1280 / 720' }}>
            <svg width="100%" height="100%" viewBox="0 0 1280 720" preserveAspectRatio="xMidYMid meet"
              style={{ position: 'absolute', top: 0, left: 0, zIndex: 1, pointerEvents: 'none' }}>
              {/* Paths */}
              <path id="p0" d="M 280 360 L 440 360" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p1" d="M 760 360 Q 855 216, 950 72" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p2" d="M 760 360 Q 855 258, 950 156" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p3" d="M 760 360 Q 855 301, 950 241" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p4" d="M 760 360 Q 855 343, 950 325" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p5" d="M 760 360 Q 855 385, 950 409" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p6" d="M 760 360 Q 855 427, 950 493" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p7" d="M 760 360 Q 855 469, 950 578" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />
              <path id="p8" d="M 760 360 Q 855 511, 950 662" stroke="#6366f1" strokeWidth="3" strokeDasharray="8,5" fill="none" />

              {/* Endpoint dots */}
              {[
                [280,360],[440,360],[760,360],
                [950,72],[950,156],[950,241],[950,325],[950,409],[950,493],[950,578],[950,662],
              ].map(([cx,cy], i) => (
                <circle key={i} cx={cx} cy={cy} r="5" fill="#6366f1" />
              ))}

              {/* Animated dots */}
              {[
                { path: '#p0', dur: '8s', begin: '0s' },
                { path: '#p1', dur: '10s', begin: '1s' },
                { path: '#p2', dur: '10s', begin: '2s' },
                { path: '#p3', dur: '10s', begin: '3s' },
                { path: '#p4', dur: '10s', begin: '4s' },
                { path: '#p5', dur: '10s', begin: '5s' },
                { path: '#p6', dur: '10s', begin: '6s' },
                { path: '#p7', dur: '10s', begin: '7s' },
                { path: '#p8', dur: '10s', begin: '8s' },
              ].map((d, i) => (
                <circle key={i} r="8" fill="#6366f1" opacity="0.7" style={{ filter: 'drop-shadow(0 0 10px rgba(99,102,241,0.5))' }}>
                  <animateMotion dur={d.dur} begin={d.begin} repeatCount="indefinite">
                    <mpath href={d.path} />
                  </animateMotion>
                </circle>
              ))}
            </svg>

            {/* Employees Box */}
            <div className="absolute z-10" style={{ left: '6.25%', top: '43.06%', width: '15.625%' }}>
              <div className="bg-gradient-to-br from-indigo-600 to-violet-700 rounded-xl p-6 text-center shadow-lg">
                <Users className="w-10 h-10 text-indigo-200 mx-auto mb-2" />
                <h3 className="text-xl font-bold text-white">Employees</h3>
                <p className="text-indigo-200 text-sm mt-1">Voice/Teams/IVR</p>
              </div>
            </div>

            {/* Voice Agents Box */}
            <div className="absolute z-10" style={{ left: '34.375%', top: '39.58%', width: '25%' }}>
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-8 text-center shadow-xl border-3 border-indigo-300">
                <Brain className="w-12 h-12 text-white mx-auto mb-2" />
                <h3 className="text-2xl font-bold text-white">digian.ai</h3>
                <h4 className="text-lg font-semibold text-indigo-200">Voice Agents</h4>
                <p className="text-white text-sm mt-2">AI-Powered Routing</p>
              </div>
            </div>

            {/* Integration boxes (right side) */}
            {integrationCategories.map((cat, i) => {
              const Icon = cat.icon;
              const topPercents = ['6.94%', '18.61%', '30.42%', '42.08%', '53.75%', '65.42%', '77.22%', '88.89%'];
              return (
                <div key={i} className="absolute z-10" style={{ left: '74.22%', top: topPercents[i], width: '19.53%' }}>
                  <div className="bg-gradient-to-r from-indigo-700 to-violet-800 rounded-lg px-5 py-3 flex items-center gap-3 shadow-md">
                    <Icon className="w-6 h-6 text-indigo-200 flex-shrink-0" />
                    <span className="text-white font-semibold">{cat.label}</span>
                  </div>
                </div>
              );
            })}
          </motion.div>

          {/* Mobile fallback: list layout */}
          <div className="lg:hidden space-y-8 mt-8">
            {integrationCategories.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}>
                  <h3 className="text-xl font-bold text-indigo-600 flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5" /> {cat.label}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {cat.tags.map((tag) => (
                      <span key={tag} className="bg-white border border-slate-200 text-slate-800 px-4 py-2 rounded-full text-sm font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 10: Telephony
         ══════════════════════════════════════════════════════════ */}
      <section id="telephony" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Telephony & Voice <span className="text-indigo-600">Platform Support</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Enterprise Telephony Compatibility</p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {telephonyProviders.map((provider, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                className={provider.logo
                  ? 'bg-white border border-slate-200 rounded-xl p-6 text-center shadow-md flex flex-col items-center justify-center'
                  : `bg-gradient-to-br ${i % 3 === 0 ? 'from-indigo-500 to-purple-500' : i % 3 === 1 ? 'from-purple-500 to-pink-500' : 'from-violet-500 to-indigo-500'} rounded-xl p-6 text-center text-white shadow-lg flex flex-col items-center justify-center`}>
                {provider.logo ? (
                  <img src={provider.logo} alt={provider.name} className="w-20 h-20 mx-auto mb-3 object-contain" />
                ) : (
                  <Phone className="w-10 h-10 mx-auto mb-2" />
                )}
                <h4 className={`font-bold ${provider.logo ? 'text-slate-800' : ''}`}>{provider.name}</h4>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-8 bg-indigo-50 border-l-4 border-indigo-500 rounded-xl p-5">
            <p className="text-slate-800 font-medium flex items-center gap-2">
              <Plug className="w-5 h-5 text-indigo-600" />
              Seamless integration with existing call center infrastructure
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 11: Security
         ══════════════════════════════════════════════════════════ */}
      <section id="security" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Security, Governance & <span className="text-indigo-600">Compliance</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Enterprise-Grade Security</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityCards.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-xl border-l-4 border-indigo-500 p-6 shadow-sm">
                  <h4 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-2">
                    <Icon className="w-6 h-6 text-indigo-600" /> {s.title}
                  </h4>
                  <p className="text-slate-600">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 12: Business Impact
         ══════════════════════════════════════════════════════════ */}
      <section id="impact" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Measurable <span className="text-indigo-600">Business Impact</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Expected Outcomes</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {impactCards.map((c, i) => {
              const Icon = c.icon;
              const gradients = [
                'from-indigo-500 to-purple-500',
                'from-purple-500 to-pink-500',
                'from-violet-500 to-indigo-500',
                'from-indigo-600 to-violet-600',
              ];
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                  className={`bg-gradient-to-br ${gradients[i % gradients.length]} rounded-xl p-6 text-center text-white shadow-lg`}>
                  <Icon className="w-10 h-10 mx-auto mb-2 opacity-90" />
                  <h3 className="text-3xl font-extrabold">{c.number}</h3>
                  <p className="font-semibold mt-1">{c.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 13: Roadmap
         ══════════════════════════════════════════════════════════ */}
      <section id="roadmap" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-10">
              Implementation <span className="text-indigo-600">Roadmap</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {phases.map((phase, i) => {
              const colors = ['border-indigo-500', 'border-purple-500', 'border-violet-500'];
              const bgColors = ['bg-indigo-600', 'bg-purple-600', 'bg-violet-600'];
              const textColors = ['text-indigo-600', 'text-purple-600', 'text-violet-600'];
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className={`bg-white rounded-xl border-2 ${colors[i]} p-6 shadow-sm`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 rounded-full ${bgColors[i]} text-white flex items-center justify-center text-xl font-extrabold flex-shrink-0`}>
                      {phase.number}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{phase.title}</h3>
                      <span className={`text-sm font-semibold ${textColors[i]}`}>{phase.timeline}</span>
                    </div>
                  </div>
                  <ul className="space-y-2 pl-16">
                    {phase.items.map((item, j) => (
                      <li key={j} className="flex items-center gap-2 text-slate-700">
                        <CheckCircle2 className={`w-4 h-4 ${textColors[i]}`} /> {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 14: Summary & Next Steps
         ══════════════════════════════════════════════════════════ */}
      <section id="summary" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Summary & <span className="text-indigo-600">Next Steps</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Executive Takeaways</p>
          </motion.div>

          <div className="space-y-4 mb-12">
            {executiveTakeaways.map((t, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4 p-5 bg-white rounded-xl border-l-4 border-indigo-500">
                <CheckCircle2 className="w-6 h-6 text-indigo-600 flex-shrink-0 mt-0.5" />
                <p className="text-slate-800"><strong className="text-slate-900">{t.bold}</strong> {t.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="bg-indigo-50 border-2 border-indigo-500 rounded-xl p-8">
            <h3 className="text-xl font-bold text-indigo-600 flex items-center gap-2 mb-6">
              <Rocket className="w-6 h-6" /> Recommended Pilot
            </h3>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-bold text-slate-900 mb-3">Pilot Scope (Phase 1):</h4>
                <ul className="space-y-2">
                  {pilotScope.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-indigo-600" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-3">Success Metrics:</h4>
                <div className="flex flex-wrap gap-2">
                  {successMetrics.map((m) => (
                    <span key={m} className="bg-white border border-slate-200 text-indigo-600 px-4 py-2 rounded-full text-sm font-medium">
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeUp}
            className="mt-8 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-center text-white shadow-lg">
            <p className="text-lg font-semibold flex items-center justify-center gap-2">
              <ArrowRight className="w-5 h-5" /> Next: Confirm target use cases + systems, define pilot workflows, and schedule a live demo with your environment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          Slide 15: Comparison / Decision Matrix
         ══════════════════════════════════════════════════════════ */}
      <section id="comparison" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-1">
                digian.ai vs <span className="text-indigo-600">Traditional Helpdesk</span>
              </h2>
              <p className="text-lg text-slate-600">Why AI Voice Automation Outperforms Legacy Support</p>
            </div>
            <span className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-600 px-5 py-2.5 rounded-lg text-sm font-bold whitespace-nowrap">
              <Scale className="w-4 h-4" /> Decision Matrix
            </span>
          </motion.div>

          <motion.div {...fadeUp} className="bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200">
            <div className="grid grid-cols-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-bold text-sm lg:text-base">
              <div className="p-4 lg:p-5 border-r border-white/20">EVALUATION CRITERIA</div>
              <div className="p-4 lg:p-5 border-r border-white/20">Traditional Helpdesk</div>
              <div className="p-4 lg:p-5 relative">
                <Bot className="w-4 h-4 inline mr-1" />digian.ai Platform
                <span className="absolute top-2 right-2 bg-white/20 text-white text-[10px] font-bold px-2 py-0.5 rounded hidden lg:inline">RECOMMENDED</span>
              </div>
            </div>

            {comparisonRows.map((row, i) => {
              const TradIcon = row.tradIcon;
              const DigianIcon = row.digianIcon;
              const tradBg = row.tradColor === 'red' ? 'bg-red-50' : 'bg-amber-50';
              const tradIconColor = row.tradColor === 'red' ? 'text-red-500' : 'text-amber-500';
              return (
                <div key={i} className={`grid grid-cols-3 border-b border-slate-200 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                  <div className="p-4 lg:p-5 font-semibold text-slate-800 border-r border-slate-200 flex items-center text-sm lg:text-base">
                    {row.criteria}
                  </div>
                  <div className={`p-4 lg:p-5 ${tradBg} border-r border-slate-200 flex items-center gap-2 text-sm lg:text-base`}>
                    <TradIcon className={`w-5 h-5 ${tradIconColor} flex-shrink-0`} />
                    <span className="text-slate-600">{row.traditional}</span>
                  </div>
                  <div className="p-4 lg:p-5 bg-indigo-50 flex items-center gap-2 text-sm lg:text-base">
                    <DigianIcon className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <span className="text-indigo-900 font-semibold">{row.digian}</span>
                  </div>
                </div>
              );
            })}
          </motion.div>

          <motion.div {...fadeUp}
            className="mt-8 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 text-white shadow-lg flex items-center gap-4">
            <Info className="w-8 h-8 text-indigo-200 flex-shrink-0" />
            <p>
              <strong>Key Advantage:</strong> digian.ai transforms IT support from a reactive cost center into a proactive, automated service experience.
            </p>
          </motion.div>
        </div>
      </section>


      {/* ── FAQ ── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: 'Which ITSM platforms do you integrate with?', a: 'Digian.ai AI Voice Agent integrates natively with ServiceNow, Jira Service Management, Freshservice, Zendesk, and more. Custom integrations are available on Enterprise plans.' },
              { q: 'What telephony providers are supported?', a: 'We support Twilio, Genesys, Cisco, Avaya, RingCentral, and most SIP-based providers.' },
              { q: 'How long does it take to deploy?', a: 'Standard deployments are live within 2–3 weeks. Enterprise deployments with custom integrations typically take 4–6 weeks with a dedicated implementation engineer.' },
              { q: 'Can the AI escalate to a human agent?', a: 'Yes. The AI detects when a query requires human expertise and transfers seamlessly to a live agent, passing full call context, transcript, and ticket data.' },
              { q: 'How is the AI trained on our data?', a: 'You provide your knowledge base, IT policies, and historical ticket data during onboarding. The AI is fine-tuned on your content without sharing data with other customers.' },
              { q: 'How is data secured?', a: 'All voice data is encrypted in transit and at rest. We follow GDPR-aligned data handling practices, include full audit logging, and support on-premise deployment for Enterprise customers requiring full data sovereignty.' },
            ].map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Footer ── */}
      <section className="bg-gradient-to-r from-indigo-600 to-violet-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">
              Ready to Transform Your <span className="text-indigo-200">IT Helpdesk?</span>
            </h2>
            <p className="text-xl text-indigo-100">
              Transforming IT Helpdesk with AI Voice & Intelligent Automation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" variant="secondary">
                  Request Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-indigo-600">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIVoiceAgentPage;
